import { useEffect, useState } from 'react';
import { Loader2, BarChart3, MessageSquare, ArrowLeftRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import useTranslation from '@/hooks/useTranslation';
import useGlobalProgressStore, { ProgressEntry } from '@/store/GlobalProgressStore';
import useResultTabStore from '@/store/ResultsTabStore';

const ICON_MAP: Record<ProgressEntry['type'], typeof Loader2> = {
  analysis: BarChart3,
  research_assistant: MessageSquare,
  comparison: ArrowLeftRight,
};

const TAB_MAP: Record<ProgressEntry['type'], string> = {
  analysis: 'overview_tab',
  research_assistant: 'ai_search_tab',
  comparison: 'compare_tab',
};

function ElapsedTime({ startedAt }: { startedAt: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return (
    <span className="text-[11px] text-neutral-400 tabular-nums">
      {mins > 0 ? `${mins}m ${secs.toString().padStart(2, '0')}s` : `${secs}s`}
    </span>
  );
}

function GlobalProgressBar() {
  const { lang } = useTranslation();
  const entries = useGlobalProgressStore((s) => s.entries);
  const setActiveTab = useResultTabStore((s) => s.setActiveTab);

  if (entries.length === 0) return null;

  return (
    <div className="fixed left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm"
      style={{ top: 55 }}
    >
      {entries.map((entry) => {
        const Icon = ICON_MAP[entry.type] || Loader2;
        return (
          <button
            key={entry.id}
            type="button"
            onClick={() => setActiveTab(TAB_MAP[entry.type] || 'overview_tab')}
            className="flex items-center gap-2.5 w-full px-4 py-2 hover:bg-neutral-50 transition-colors text-left"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-600 shrink-0" />
            <Icon className="h-3.5 w-3.5 text-neutral-500 shrink-0" />
            <span className="text-xs font-medium text-neutral-700 truncate">
              {entry.label}
            </span>
            {entry.progress && (
              <span className="text-[11px] text-neutral-500">
                {entry.progress.step}
                {entry.progress.total > 0 &&
                  ` ${entry.progress.completed}/${entry.progress.total}`}
              </span>
            )}
            <ElapsedTime startedAt={entry.startedAt} />
          </button>
        );
      })}
    </div>
  );
}

export default GlobalProgressBar;
